import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaAngleLeft, FaAngleRight, FaThLarge } from 'react-icons/fa';
import productCategory from '../helpers/productCategory';
import MegaMenu from './MegaMenu';
import SummaryApi from '../common';

const CategoryNav = () => {
  const navigate = useNavigate();
  const stripRef = useRef(null);
  const itemsRef = useRef([]);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [openMega, setOpenMega] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  const hoverTimer = useRef(null);

  useEffect(() => {
    fetch(SummaryApi.publicCategoryTree.url, { credentials: 'include' })
      .then(r => r.json())
      .then(json => {
        if (json?.success && Array.isArray(json.data) && json.data.length > 0) {
          setDbCategories(json.data);
        }
      })
      .catch(() => {});
  }, []);

  const categoriesToDisplay = useMemo(() => {
    if (dbCategories.length > 0) {
      return dbCategories.map(c => ({ label: c.name, value: c.name, slug: c.slug }));
    }
    return productCategory.map(c => ({ label: c.label, value: c.value, slug: c.value }));
  }, [dbCategories]);

  const updateArrows = () => {
    const el = stripRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 2);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 2);
  };

  useEffect(() => {
    updateArrows();
    const el = stripRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => updateArrows();
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [categoriesToDisplay]);

  const scrollByAmount = (dx) => {
    const el = stripRef.current;
    if (!el) return;
    try {
      el.scrollBy({ left: dx, behavior: 'smooth' });
    } catch (_) {
      el.scrollLeft = el.scrollLeft + dx;
    }
  };

  return (
    <nav
      className='bg-white border-t border-b border-slate-200 relative select-none z-30'
      onMouseEnter={() => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
      }}
      onMouseLeave={() => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setOpenMega(false), 150);
      }}
    >
      <div className='container mx-auto px-4 h-12 flex items-center gap-3'>
        {/* Mega Menu Trigger */}
        <button
          className='whitespace-nowrap text-sm font-bold text-slate-900 px-3 py-1.5 rounded-xl hover:bg-slate-100 flex items-center gap-2 transition-colors border border-slate-200 shadow-sm'
          onClick={() => setOpenMega(v => !v)}
          onMouseEnter={() => setOpenMega(true)}
          aria-expanded={openMega}
          aria-controls='mega-menu'
        >
          <FaThLarge className='text-brand-600 text-xs' />
          Shop by Category
        </button>

        {/* Scrollable Category Bar */}
        <div className='relative flex-1 overflow-hidden'>
          {canLeft && (
            <div className='absolute left-0 top-0 h-full w-10 bg-gradient-to-r from-white to-transparent pointer-events-none z-10' />
          )}
          <button
            type='button'
            onClick={() => scrollByAmount(-300)}
            className={`absolute left-0 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center text-xs z-20 transition-opacity ${canLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            aria-label='Scroll left'
          >
            <FaAngleLeft />
          </button>

          <div
            ref={stripRef}
            className='flex items-center gap-2 overflow-x-auto scrollbar-none py-1'
            style={{ scrollBehavior: 'smooth' }}
          >
            {categoriesToDisplay.map((cat, idx) => (
              <Link
                key={cat.value + idx}
                to={`/product-category?category=${encodeURIComponent(cat.value)}`}
                ref={(el) => (itemsRef.current[idx] = el)}
                className='whitespace-nowrap text-xs font-semibold px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-brand-50 text-slate-700 hover:text-brand-700 transition-all border border-slate-200/60'
              >
                {cat.label}
              </Link>
            ))}
          </div>

          {canRight && (
            <div className='absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent pointer-events-none z-10' />
          )}
          <button
            type='button'
            onClick={() => scrollByAmount(300)}
            className={`absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center text-xs z-20 transition-opacity ${canRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            aria-label='Scroll right'
          >
            <FaAngleRight />
          </button>
        </div>
      </div>

      {/* Mega menu panel */}
      <MegaMenu id='mega-menu' open={openMega} onClose={() => setOpenMega(false)} />
    </nav>
  );
};

export default CategoryNav;
